'use client';
import React, { useEffect, useRef } from 'react';
import { LazyMotion, m as motion, useAnimation } from 'framer-motion';
import { variantsFadeInUp } from '../__constants__/animation-variants';
import { useInView } from 'react-cool-inview';
import { useIsMobile } from '../hooks/useResponsive';

const loadFeatures = () => import('../__constants__/framer-features').then((res) => res.default);

type AnimationFadeInProps = {
    children: JSX.Element | JSX.Element[];
    className?: string;
    threshold?: number;
};

// Circle content doesn't fade out after scrolled past - used in CTAFullSimple
// CTA circle at bottom of the page
export const AnimationFadeIn = (props: AnimationFadeInProps) => {
    const { isMobile } = useIsMobile();
    const { children, className, threshold = isMobile ? 0.2 : 0.35, ...rest } = props;
    const controls = useAnimation();

    // Use ref for timeout to avoid setState in effect
    const safetyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Set a safety timeout to make elements visible after 2 seconds
        // This ensures components appear even if intersection observer fails
        safetyTimeoutRef.current = setTimeout(() => {
            controls.start('visible');
        }, 2000);

        return () => {
            // Clear the timeout on cleanup
            if (safetyTimeoutRef.current) {
                clearTimeout(safetyTimeoutRef.current);
            }
        };
    }, [controls]);

    // checks if is in view on load
    const { observe: observeContainer } = useInView({
        onEnter: ({ entry, unobserve }) => {
            if (entry.isIntersecting) {
                controls.start('visible');

                // Clear safety timeout since we're visible now
                if (safetyTimeoutRef.current) {
                    clearTimeout(safetyTimeoutRef.current);
                }

                unobserve();
            }
        },
        onChange: ({ unobserve }) => {
            unobserve(); // To stop observing the current target element
        },
    });

    const { observe } = useInView({
        threshold,
        onEnter: ({ scrollDirection }) => {
            if (scrollDirection.vertical === 'up') {
                controls.start('visible');

                // Clear safety timeout since we're visible now
                if (safetyTimeoutRef.current) {
                    clearTimeout(safetyTimeoutRef.current);
                }
            }
        },
        onLeave: ({ scrollDirection }) => {
            if (scrollDirection.vertical === 'down') {
                controls.start('hidden');
            }
        },
    });

    return (
        <div className="relative" ref={observeContainer}>
            <LazyMotion features={loadFeatures}>
                <motion.div
                    className={`relative ${className || ''}`}
                    ref={observe}
                    animate={controls}
                    initial="hidden"
                    variants={variantsFadeInUp}
                    {...rest}
                >
                    {children}
                </motion.div>
            </LazyMotion>
        </div>
    );
};

export default AnimationFadeIn;
